import {ApolloQueryResult, gql} from '@apollo/client';
import {createStyles} from '@mantine/core';
import React, {useEffect, useMemo} from 'react';

import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import {assert} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {useTimedAttentionIds} from 'procedures/hooks/useTimedAttentionIds';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {Column, Table} from 'ui-library/simple-table/SimpleTable';
import {CenteredSpinner} from 'ui-library/Spinner';
import {InputChangeEvent} from 'util/Types';

import {isLibraryParameterDefinitionDataType, LibraryParameterDefinition} from '../ParameterLibraryTypes';
import {
  ParameterLibraryListingQuery,
  ParameterLibraryListingQueryVariables,
} from './__generated/ParameterLibraryListingQuery';
import {ParameterLibraryEmptyState} from './ParameterLibraryEmptyState';
import {
  ActionButtonsDisplayCell,
  ActionButtonsEditCell,
  DataTypeDisplayCell,
  DataTypeEditCell,
  DescriptionDisplayCell,
  DescriptionEditCell,
  NameDisplayCell,
  NameEditCell,
  UnitTypeDisplayCell,
  UnitTypeEditCell,
} from './ParameterLibraryListingCells';
import {archiveParameterDefinition, unarchiveParameterDefinition} from './parameterLibraryListingUtils';
import {useFilteredParameterDefinitions} from './useFilteredParameterDefinitions';
import {useParameterDefinitionDraft} from './useParameterDefinitionDraft';

const useStyles = createStyles(() => ({
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    // Match @input-height-base, so the action buttons are aligned with the input fields
    minHeight: 30,
  },
  archivedField: {
    opacity: 0.6,
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginTop: '6px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  search: {
    width: 300,
    verticalAlign: 'middle',
    marginRight: '10px',
  },
  filteringControls: {
    display: 'flex',
  },
  addButton: {
    marginLeft: '10px',
  },
  auditAndAddButtons: {
    display: 'flex',
  },
  descriptionCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
  },
  newlyCreatedParameterDefinitionRow: {
    '& > td': {
      backgroundColor: '#f1f3f5',
    },
  },
  emptyTableCell: {
    // Zero out the default .simpleTable-cell padding so the inner div fills the full cell.
    '& .simpleTable-row--empty .simpleTable-cell': {
      padding: 0,
      backgroundColor: GRAY_LIGHTER,
    },
  },
  /** Top-align so cells with different content heights don't vertically center unevenly. */
  editRowCell: {
    verticalAlign: 'top',
  },
}));

const NEW_PARAMETER_DEFINITION_HIGHLIGHT_DURATION_MS = 10_000;
export const NEWLY_CREATED_PARAMETER_DEFINITION_ROW_CLASS =
  'parameterLibraryListing-newlyCreatedParameterDefinitionRow';

export const PARAMETER_LIBRARY_LISTING_QUERY = gql`
  query ParameterLibraryListingQuery @skipAudit @engComponent(name: PROCEDURES) {
    allLibraryParameterDefinitions {
      ...LibraryParameterDefinitionGraphQL
    }
    currentTenant {
      id
      permissions {
        canCreateLibraryParameterDefinitions
      }
    }
  }
  ${getFragmentWrapper('LibraryParameterDefinition').fragment}
`;

export default function ParameterLibraryListing(): JSX.Element {
  const queryResult = useBenchlingQuery<ParameterLibraryListingQuery, ParameterLibraryListingQueryVariables>(
    PARAMETER_LIBRARY_LISTING_QUERY,
    {renderCachedDataWhenRefetching: true}
  );

  useEffect(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      queryResult.data.allLibraryParameterDefinitions.forEach((parameterDefinition) => {
        assert(
          isLibraryParameterDefinitionDataType(parameterDefinition.dataType),
          `Unexpected data type for library parameter definition: ${parameterDefinition.dataType}`
        );
      });
    }
  }, [queryResult]);

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Failed to load parameter library" />;
  }
  return (
    <ParameterLibraryListingDisplay
      parameterDefinitions={
        queryResult.data.allLibraryParameterDefinitions as unknown as Array<LibraryParameterDefinition>
      }
      refetch={queryResult.refetch}
      canCreateLibraryParameterDefinitions={
        queryResult.data.currentTenant.permissions.canCreateLibraryParameterDefinitions
      }
    />
  );
}

/**
 * Presentational component for parameter library listing.
 * @package
 */
export function ParameterLibraryListingDisplay({
  parameterDefinitions,
  canCreateLibraryParameterDefinitions,
  refetch,
}: {
  parameterDefinitions: Array<LibraryParameterDefinition>;
  canCreateLibraryParameterDefinitions: boolean;
  refetch: () => Promise<ApolloQueryResult<ParameterLibraryListingQuery>>;
}): JSX.Element {
  const {markIdsForAttention: markParameterDefinitionRowsForAttention, shouldDrawAttention} =
    useTimedAttentionIds(NEW_PARAMETER_DEFINITION_HIGHLIGHT_DURATION_MS);

  const {
    filteredParameterDefinitions,
    searchQuery,
    setSearchQuery,
    selectedArchivePurposes,
    selectArchivePurposes,
  } = useFilteredParameterDefinitions(parameterDefinitions);

  const allParameterDefinitionsIds = parameterDefinitions.map((pd) => pd.id);

  const {
    draftParameterDefinition,
    updateDraftParameterDefinition,
    isEditing,
    isCreating,
    isEditingExisting,
    editingDefinitionId,
    isSaving,
    nameErrorMessage,
    descriptionErrorMessage,
    unitTypeErrorMessage,
    dropdownErrorMessage,
    isValid,
    startCreating,
    startEditingDefinition,
    onCancel,
    onSave,
    handleRowKeyDown,
  } = useParameterDefinitionDraft({
    parameterDefinitions,
    refetch,
    markParameterDefinitionRowsForAttention,
  });

  const selectorsById = useSelectorsById({includeArchivedSelectors: false});
  const dropdownOptions = useMemo(() => {
    if (!selectorsAreLoaded(selectorsById)) {
      return [];
    }
    return Object.values(selectorsById).map((selector) => ({
      id: selector.id,
      name: getArchiveAwareDisplayName(selector),
    }));
  }, [selectorsById]);

  const {classes} = useStyles();

  /**
   * Translates a table row index to the corresponding index in filteredParameterDefinitions.
   * When creating, the draft row occupies index 0, so existing data rows are shifted by 1.
   * When editing an existing row, no extra row is added so the index is 1:1.
   */
  function getDataRowIndex(rowIndex: number): number {
    return isCreating ? rowIndex - 1 : rowIndex;
  }

  function isNewParameterDefinitionRow(rowIndex: number): boolean {
    return isCreating && rowIndex === 0;
  }

  function isEditingRow(rowIndex: number): boolean {
    if (!isEditingExisting) {
      return false;
    }
    const dataRowIndex = getDataRowIndex(rowIndex);
    return filteredParameterDefinitions[dataRowIndex]?.id === editingDefinitionId;
  }

  function getArchivedClass(rowIndex: number): string | undefined {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return undefined;
    }
    return filteredParameterDefinitions[getDataRowIndex(rowIndex)].archiveData.archived
      ? classes.archivedField
      : undefined;
  }

  function getRowClassName(rowIndex: number): string {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return '';
    }

    return shouldDrawAttention(filteredParameterDefinitions[getDataRowIndex(rowIndex)].id)
      ? `${classes.newlyCreatedParameterDefinitionRow} ${NEWLY_CREATED_PARAMETER_DEFINITION_ROW_CLASS}`
      : '';
  }

  function renderNameCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex) || isEditingRow(rowIndex)) {
      return (
        <NameEditCell
          name={draftParameterDefinition.name}
          onChangeName={(name) => {
            updateDraftParameterDefinition({name});
          }}
          onKeyDown={handleRowKeyDown}
          nameErrorMessage={nameErrorMessage}
          className={classes.editRowCell}
        />
      );
    }
    const pd = filteredParameterDefinitions[getDataRowIndex(rowIndex)];
    return <NameDisplayCell parameterDefinition={pd} className={getArchivedClass(rowIndex)} />;
  }

  function renderDataTypeCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return (
        <DataTypeEditCell
          selectedType={draftParameterDefinition.dataType}
          onChangeType={(newType) => {
            updateDraftParameterDefinition({
              dataType: newType,
              // Clear unit type if switching away from decimal
              unitTypeId: newType === FieldDefinitionType.FLOAT ? draftParameterDefinition.unitTypeId : null,
              schemaFieldSelectorId:
                newType === FieldDefinitionType.DROPDOWN
                  ? draftParameterDefinition.schemaFieldSelectorId
                  : null,
              selectedDropdownName:
                newType === FieldDefinitionType.DROPDOWN
                  ? draftParameterDefinition.selectedDropdownName
                  : null,
            });
          }}
          onSelectDropdown={(selectedSelector) => {
            updateDraftParameterDefinition({
              schemaFieldSelectorId: selectedSelector?.id ?? null,
              selectedDropdownName: selectedSelector?.name ?? null,
            });
          }}
          selectedDropdownName={draftParameterDefinition.selectedDropdownName ?? null}
          dropdownOptions={dropdownOptions}
          dropdownErrorMessage={dropdownErrorMessage}
          className={classes.editRowCell}
        />
      );
    }
    const pd = filteredParameterDefinitions[getDataRowIndex(rowIndex)];
    return (
      <DataTypeDisplayCell
        dataType={pd.dataType}
        dropdownName={pd.dropdown?.name ?? undefined}
        className={getArchivedClass(rowIndex)}
      />
    );
  }

  function renderUnitTypeCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return (
        <UnitTypeEditCell
          selectedId={draftParameterDefinition.unitTypeId}
          onSelect={(selectedUnitType) => {
            updateDraftParameterDefinition({unitTypeId: selectedUnitType?.id ?? null});
          }}
          disabled={draftParameterDefinition.dataType !== FieldDefinitionType.FLOAT}
          unitTypeErrorMessage={unitTypeErrorMessage}
          className={classes.editRowCell}
        />
      );
    }
    const pd = filteredParameterDefinitions[getDataRowIndex(rowIndex)];
    return <UnitTypeDisplayCell unitTypeName={pd.unitType?.name} className={getArchivedClass(rowIndex)} />;
  }

  function renderDescriptionCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex) || isEditingRow(rowIndex)) {
      return (
        <DescriptionEditCell
          description={draftParameterDefinition.description}
          onChangeDescription={(description) => {
            updateDraftParameterDefinition({description});
          }}
          onKeyDown={handleRowKeyDown}
          descriptionErrorMessage={descriptionErrorMessage}
          descriptionCellClassName={classes.descriptionCell}
          className={classes.editRowCell}
        />
      );
    }
    const pd = filteredParameterDefinitions[getDataRowIndex(rowIndex)];
    return (
      <DescriptionDisplayCell
        description={pd.description ?? ''}
        className={getArchivedClass(rowIndex)}
        descriptionCellClassName={classes.descriptionCell}
      />
    );
  }

  function renderFieldActionButtonsCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex) || isEditingRow(rowIndex)) {
      return (
        <ActionButtonsEditCell
          actionButtonsClassName={classes.actionButtons}
          isEditing={isEditing}
          isSaving={isSaving}
          isValid={isValid}
          onCancel={onCancel}
          onComplete={onSave}
          className={classes.editRowCell}
        />
      );
    }
    const pd = filteredParameterDefinitions[getDataRowIndex(rowIndex)];
    return (
      <ActionButtonsDisplayCell
        actionButtonsClassName={classes.actionButtons}
        parameterDefinition={pd}
        isEditing={isEditing}
        onEdit={canCreateLibraryParameterDefinitions ? startEditingDefinition : undefined}
        onArchive={canCreateLibraryParameterDefinitions ? archiveParameterDefinition : undefined}
        onUnarchive={canCreateLibraryParameterDefinitions ? unarchiveParameterDefinition : undefined}
      />
    );
  }

  return (
    <div {...getTestProps({component: 'ParameterLibraryListing', element: 'root'})}>
      <div className={classes.tableControls}>
        <div className={classes.filteringControls}>
          <PlainSearchInput
            className={classes.search}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              onCancel();
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              onCancel();
              setSearchQuery('');
            }}
            placeholder="Search"
            value={searchQuery}
          />
          <ArchivePurposeDropdown
            itemIds={allParameterDefinitionsIds}
            selectedPurposes={selectedArchivePurposes}
            onChangePurposes={(newPurposes) => {
              onCancel();
              selectArchivePurposes(newPurposes);
            }}
          />
        </div>
        <div className={classes.auditAndAddButtons}>
          <Tooltip text={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
            <Button
              icon="audit-trail"
              onClick={() => {
                exportAuditLogs(allParameterDefinitionsIds);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          </Tooltip>
          {canCreateLibraryParameterDefinitions && (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              text="Add"
              icon="compose"
              className={classes.addButton}
              onClick={() => {
                startCreating();
              }}
              disabled={isEditing || isSaving}
              dataTestAttrs={{component: 'ParameterLibraryListing', element: 'addButton'}}
            />
          )}
        </div>
      </div>
      <Table
        className={classes.emptyTableCell}
        frozenHeader={true}
        overflowVisible={true}
        showBorder={false}
        getRowClassName={getRowClassName}
        numRows={isCreating ? filteredParameterDefinitions.length + 1 : filteredParameterDefinitions.length}
        emptyTableText={<ParameterLibraryEmptyState />}
      >
        <Column header="Name" renderCell={renderNameCell} />
        <Column header="Data type" renderCell={renderDataTypeCell} />
        <Column header="Unit type" renderCell={renderUnitTypeCell} />
        <Column header="Description" renderCell={renderDescriptionCell} />
        <Column
          // no header for action buttons
          header=""
          renderCell={renderFieldActionButtonsCell}
        />
      </Table>
    </div>
  );
}
